import { useEffect, useRef, type JSX, type ReactNode } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title?: string;
  message?: string;
  confirmLabel?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  /**
   * Optional slot rendered between the message and the action buttons. Use
   * sparingly — for ergonomic confirmations that need a single inline control
   * (e.g. a notify-submitter checkbox on revision cancel). When you'd need
   * multiple inputs or validation, build a dedicated modal instead. Day 56
   * COMPONENT_STANDARDS §6.
   */
  extraContent?: ReactNode;
  /**
   * When true, the Confirm button renders disabled and onConfirm cannot fire.
   * Pair with `extraContent` containing a gate input (e.g. type-RESET to
   * confirm). Default false preserves the single-action contract for all
   * existing callers. Day 92b G.10 — typed-confirm gate.
   */
  confirmDisabled?: boolean;
}

/**
 * Modal confirmation dialog styled in Arbi UI.
 * Renders as an overlay when `open` is true.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  onConfirm,
  onCancel,
  extraContent,
  confirmDisabled = false,
}: ConfirmDialogProps): JSX.Element | null {
  const confirmRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open && confirmRef.current) confirmRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    // Day 98f Part 1 — z lifted to the modal token +1 so a ConfirmDialog spawned
    // from a token-z FormModal (200) always stacks above it; 201 also tops legacy z-50 modals.
    <div className="fixed inset-0 z-[calc(var(--z-modal,200)+1)] flex items-center justify-center">
      {/* Backdrop — keyboard dismiss is handled via window-level Escape listener above */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/50" onClick={onCancel} aria-hidden="true" />
      {/* Dialog */}
      <div className="relative bg-surface-2 border border-border rounded-lg shadow-xl w-80 p-5 space-y-4">
        <h3 className="text-sm font-semibold text-ink-bright">{title || 'Are you sure?'}</h3>
        <p className="text-xs text-ink-secondary leading-relaxed">{message}</p>
        {extraContent && <div className="text-xs text-ink-secondary">{extraContent}</div>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            className="arbi-btn text-xs bg-surface-4 text-ink-secondary hover:text-ink-bright hover:bg-surface-5 px-3 py-1.5"
          >
            Cancel
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            disabled={confirmDisabled}
            className="arbi-btn text-xs bg-danger text-white hover:bg-danger/80 px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-danger"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
